import { useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { format, startOfWeek } from 'date-fns';
import { toast } from 'react-toastify';
import { Box, Button, ButtonBase, Card, Container, Stack, TextField, Typography, Chip, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../../layouts/loading';
import TableWrapper from '../../../common/TableWrapper';
import { DEFAULT_TABLE_PAGE_LIMIT } from '../../../Constants/table.constants';
import { getWeeklyDeveloperScorecardReport } from '../../../services/reportService';
import { getTimeZone } from '../../../utils/timeCalculation';
import IssueBreakdownDialog from './IssueBreakdownDialog';

const getInitialDateRange = () => {
  const now = new Date();
  return {
    selection: {
      startDate: startOfWeek(now, { weekStartsOn: 1 }),
      endDate: now,
      key: 'selection',
    },
  };
};

const toDateInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const tableDefaultProps = {
  enableFullScreenToggle: false,
  enableColumnActions: false,
  enableColumnFilters: false,
  manualPagination: true,
  manualFiltering: false,
  manualSorting: true,
  muiTableContainerProps: {
    sx: { maxHeight: 640 },
  },
  muiTablePaperProps: {
    sx: { color: 'rgb(71 85 105 / var(--tw-text-opacity))' },
  },
  muiTableBodyCellProps: {
    sx: {
      px: 1, // default ~16px → now ~8px
      py: 1, // tighter vertical spacing
      fontSize: '0.8rem',
    },
  },
  muiTableHeadCellProps: {
    sx: {
      px: 1,
      py: 0.75,
      fontSize: '0.75rem',
    },
  },
};

const toFixed2 = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '0.00');
const formatDateLabel = (value) => (value ? format(new Date(value), 'dd MMM yyyy') : 'N/A');
const formatDateTimeLabel = (value) => (value ? format(new Date(value), 'dd MMM yy, hh:mm a') : 'N/A');
const getSurveyAnswerPreview = (row, questionId) => {
  if (!row?.latestSurvey) return 'N/A';
  const value = row.latestSurvey?.answersByQuestionId?.[questionId];
  return typeof value === 'string' && value.trim() ? value : 'No response';
};

const SUBMISSION_STATUS_OPTIONS = [
  { value: 'filled', label: 'Filled' },
  { value: 'not_filled', label: 'Not Filled' },
];

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
  filename: 'developer-scorecard-current-page',
});

export default function DeveloperScorecardTable() {
  const [draftDateRange, setDraftDateRange] = useState(getInitialDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(getInitialDateRange);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [activeSubmissionStatus, setActiveSubmissionStatus] = useState('filled');
  const [issueDialogRow, setIssueDialogRow] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_TABLE_PAGE_LIMIT,
  });
  // Stable aggregate counts — updated from every API response so the chip
  // badges never flicker to 0 while a tab-switch re-fetch is in-flight.
  const [filledCount, setFilledCount] = useState(0);
  const [notFilledCount, setNotFilledCount] = useState(0);
  const issueDialogInvokerRef = useRef(null);

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', path: '/' },
      { label: 'Admin', path: '/admin/employees-timeline' },
      { label: 'Developer Scorecard', path: '' },
    ],
    []
  );

  const { data, isError, isRefetching, isLoading } = useQuery({
    queryKey: [
      appliedDateRange.selection.startDate,
      appliedDateRange.selection.endDate,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      activeSubmissionStatus,
    ],
    queryFn: async () => {
      try {
        const response = await getWeeklyDeveloperScorecardReport(
          [],
          [],
          [],
          [],
          [],
          '',
          appliedDateRange,
          [],
          [],
          getTimeZone(),
          {
            start: pagination.pageIndex * pagination.pageSize,
            pageSize: pagination.pageSize,
            globalFilter,
            sorting,
            submissionStatus: activeSubmissionStatus,
          }
        );
        const rows = response?.rows ?? [];
        // Keep stable aggregate counts in state so chip badges don't reset
        // while a subsequent tab-switch fetch is still in-flight.
        setFilledCount(response?.filledCount ?? 0);
        setNotFilledCount(response?.notFilledCount ?? 0);
        return {
          rows,
          totalCount: response?.totalCount ?? 0,
          startDate: response?.startDate,
          endDate: response?.endDate,
          surveyQuestions: response?.surveyQuestions ?? [],
        };
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Unable to fetch developer scorecard.');
        return { rows: [], totalCount: 0, surveyQuestions: [] };
      }
    },
    placeholderData: keepPreviousData,
  });

  const questionColumns = useMemo(() => {
    const questions = Array.isArray(data?.surveyQuestions) ? data.surveyQuestions.slice(0, 2) : [];
    return [
      questions[0] || { id: 'question-1', questionText: 'Question 1' },
      questions[1] || { id: 'question-2', questionText: 'Question 2' },
    ];
  }, [data?.surveyQuestions]);

  const columns = useMemo(
    () => [
      { accessorKey: 'userName', id: 'userName', header: 'Name', size: 140 },
      {
        id: 'taskInfo',
        header: 'Task Info',
        accessorFn: (row) => row?.taskSummary || {},
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const { totalTasks = 0, completed = 0, inProgress = 0 } = row?.original?.taskSummary || {};

          return (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-line',
                lineHeight: 1.5,
              }}
            >
              {`Total Tasks: ${totalTasks}
  Completed: ${completed}
  In Progress: ${inProgress}`}
            </Typography>
          );
        },
      },

      {
        accessorFn: (row) => row?.productivity?.plannedHours ?? 0,
        id: 'plannedHours',
        header: 'Planned hrs',
        Cell: ({ cell }) => toFixed2(cell.getValue()),
        size: 80,
        enableSorting: false,
      },
      {
        accessorFn: (row) => row?.productivity?.productiveHours ?? 0,
        id: 'productiveHours',
        header: 'Productive hrs',
        Cell: ({ cell }) => toFixed2(cell.getValue()),
        size: 80,
      },
      {
        accessorFn: (row) => row?.productivity?.productivityPercent ?? 0,
        id: 'productivityPercent',
        header: 'Productivity %',
        Cell: ({ cell }) => Number(toFixed2(cell.getValue())),
        size: 95,
      },
      {
        accessorFn: (row) => row?.quality?.totalIssues ?? 0,
        id: 'totalIssues',
        header: 'Issues',
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const totalIssues = Number(cell.getValue() ?? 0);
          const isClickable = totalIssues > 0;
          const developerName = row?.original?.userName || 'Developer';

          if (!isClickable) return totalIssues;

          return (
            <ButtonBase
              component="button"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                issueDialogInvokerRef.current = event.currentTarget;
                setIssueDialogRow(row.original);
              }}
              aria-label={`View issue breakdown for ${developerName}`}
              sx={{
                typography: 'body2',
                fontWeight: 600,
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                color: 'primary.main',
                borderRadius: 1,
                px: 0.5,
                justifyContent: 'flex-start',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              {totalIssues}
            </ButtonBase>
          );
        },
        size: 85,
      },
      {
        id: `question-${questionColumns[0]?.id || '1'}`,
        header: questionColumns[0]?.questionText || 'Question 1',
        accessorFn: (row) => getSurveyAnswerPreview(row, questionColumns[0]?.id),
        size: 280,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        id: `question-${questionColumns[1]?.id || '2'}`,
        header: questionColumns[1]?.questionText || 'Question 2',
        accessorFn: (row) => getSurveyAnswerPreview(row, questionColumns[1]?.id),
        size: 280,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => row?.latestSurvey?.submittedAt || null,
        id: `submittedAt-${questionColumns[0]?.id || ''}`,
        header: 'Submitted At',
        Cell: ({ row }) => {
          const value = row?.original?.latestSurvey?.submittedAt;
          if (!value) return 'N/A';

          const dateObj = new Date(value);
        
          return (
            <Stack spacing={0}>
              <Typography variant="body2">
                {format(dateObj, 'dd MMM yy')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(dateObj, 'hh:mm a')}
              </Typography>
            </Stack>
          );
        },
        size: 110,
        enableSorting: false,
      },
      {
        accessorFn: (row) => row?.latestSurvey?.surveyDate || null,
        id: `surveyDate-${questionColumns[0]?.id || ''}`,
        header: 'Survey Date',
        Cell: ({ row }) => formatDateLabel(row?.original?.latestSurvey?.surveyDate),
        size: 120,
        enableSorting: false,
      },
    ],
    [questionColumns]
  );

  const table = useMaterialReactTable({
    columns,
    data: data?.rows ?? [],
    onPaginationChange: setPagination,
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    state: {
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    rowCount: data?.totalCount ?? 0,
    muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Error loading data' } : undefined,
    ...tableDefaultProps,
  });

  const applyDateRange = () => {
    const { startDate, endDate } = draftDateRange.selection;
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setAppliedDateRange(draftDateRange);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const initialDateRange = useMemo(() => getInitialDateRange(), []);
  const isDateRangeModified = useMemo(() => {
    const init = initialDateRange.selection;
    const applied = appliedDateRange.selection;
    return (
      new Date(applied.startDate).toDateString() !== new Date(init.startDate).toDateString() ||
      new Date(applied.endDate).toDateString() !== new Date(init.endDate).toDateString()
    );
  }, [appliedDateRange, initialDateRange]);

  const activeFilterCount = useMemo(
    () => [globalFilter, isDateRangeModified ? 'dateRange' : ''].filter(Boolean).length,
    [globalFilter, isDateRangeModified]
  );

  const handleExportCurrentPage = () => {
    if (!data?.rows?.length) return;

    const firstQuestionHeader = questionColumns[0]?.questionText || 'Question 1';
    const secondQuestionHeader = questionColumns[1]?.questionText || 'Question 2';

    const exportRows = data.rows.map((row) => ({
      Name: row?.userName || 'N/A',
      'Task Info': `Total: ${row?.taskSummary?.totalTasks ?? 0}, Completed: ${
        row?.taskSummary?.completed ?? 0
      }, In Progress: ${row?.taskSummary?.inProgress ?? 0}`,
      'Planned hrs': toFixed2(row?.productivity?.plannedHours ?? 0),
      'Productive hrs': toFixed2(row?.productivity?.productiveHours ?? 0),
      'Productivity %': toFixed2(row?.productivity?.productivityPercent ?? 0),
      Issues: row?.quality?.totalIssues ?? 0,
      'Submitted At': formatDateTimeLabel(row?.latestSurvey?.submittedAt),
      'Survey Date': formatDateLabel(row?.latestSurvey?.surveyDate),
      [firstQuestionHeader]: getSurveyAnswerPreview(row, questionColumns[0]?.id),
      [secondQuestionHeader]: getSurveyAnswerPreview(row, questionColumns[1]?.id),
    }));

    const csv = generateCsv(csvConfig)(exportRows);
    download(csvConfig)(csv);
  };

  const dateRangeLabel = useMemo(() => {
    try {
      const { startDate, endDate } = appliedDateRange?.selection || {};
      if (!startDate || !endDate) return '';
      const s = new Date(startDate).toLocaleDateString();
      const e = new Date(endDate).toLocaleDateString();
      return `${s} – ${e}`;
    } catch {
      return '';
    }
  }, [appliedDateRange]);

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl" sx={{ pb: 3 }}>
        <Loading open={isLoading} />
        <Card
          sx={{
            p: 2,
            mb: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: 'linear-gradient(135deg, rgba(255,249,235,0.95) 0%, rgba(242,248,255,0.95) 100%)',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4">Developer Scorecard</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`}
                  color="default"
                />
                <Tooltip title="Exports the scorecard currently loaded on this page">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={handleExportCurrentPage}
                      disabled={!data?.rows?.length}
                    >
                      Export page
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {SUBMISSION_STATUS_OPTIONS.map((option) => {
                // Use stable state counts (not live `data`) so badge numbers
                // don't flicker while a tab-switch re-fetch is in-flight.
                const count = option.value === 'filled' ? filledCount : notFilledCount;
                const isActive = activeSubmissionStatus === option.value;

                return (
                  <Chip
                    key={option.value}
                    label={`${option.label} (${count})`}
                    color={isActive ? 'primary' : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                    onClick={() => {
                      setActiveSubmissionStatus(option.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  />
                );
              })}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={toDateInputValue(draftDateRange.selection.startDate)}
                onChange={(event) =>
                  setDraftDateRange((prev) => ({
                    selection: {
                      ...prev.selection,
                      startDate: event.target.value ? new Date(event.target.value) : null,
                    },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={toDateInputValue(draftDateRange.selection.endDate)}
                onChange={(event) =>
                  setDraftDateRange((prev) => ({
                    selection: {
                      ...prev.selection,
                      endDate: event.target.value ? new Date(event.target.value) : null,
                    },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <Box>
                <Button variant="contained" onClick={applyDateRange}>
                  Apply
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Card>
        <Card>
          <TableWrapper>
            <MaterialReactTable table={table} />
          </TableWrapper>
        </Card>
      </Container>

      <IssueBreakdownDialog
        open={Boolean(issueDialogRow)}
        onClose={() => {
          setIssueDialogRow(null);
          // Restore focus back to the clicked cell for accessibility
          queueMicrotask(() => issueDialogInvokerRef.current?.focus?.());
        }}
        developerName={issueDialogRow?.userName || ''}
        dateRangeLabel={dateRangeLabel}
        totalIssues={issueDialogRow?.quality?.totalIssues ?? 0}
        issuesByCategory={issueDialogRow?.quality?.issuesByCategory ?? {}}
      />
    </>
  );
}
